import React from 'react';
import { useSelector } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FaqPage.module.css';

// Svar kan indeholde JSX (links til relevante sider)
const faqSections = [
  {
    heading: 'Buying',
    items: [
      {
        q: 'How does buying work?',
        a: (
          <>
            <NamedLink name="SearchPage" className={css.link}>
              Find a piece you love
            </NamedLink>{' '}
            and tap "Buy now" - or make the seller an offer. You pay through Stripe, our secure
            payment partner, and the money is held in safekeeping until your piece has arrived and
            you have confirmed it. The seller is never paid before you are happy.
          </>
        ),
      },
      {
        q: 'Can I negotiate the price?',
        a:
          'Yes. Tap "Or make an offer" on the listing and suggest your price. The seller can accept, decline or send you a counter-offer. If your offer is accepted, you simply pay the agreed price - the rest of the purchase works exactly like a normal one. Offers expire after 5 days.',
      },
      {
        q: 'What does shipping cost?',
        a:
          'Each seller sets their own shipping. Many pieces ship free - look for the shipping details on the listing. Buying from another country? See the next question.',
      },
      {
        q: "I'm buying from another country - how does it work?",
        a:
          'Choose your country on the listing. If the seller ships to your country at a listed price (many ship free worldwide), you simply buy as normal. If not, tap "Request a shipping quote" - the seller replies with the exact price for insured, tracked shipping to you, and you can then buy (or make an offer) with shipping included. All prices are in US dollars; your bank handles the conversion. Any customs or import duties charged by your country are the buyer\'s responsibility.',
      },
      {
        q: 'Is my payment safe?',
        a: (
          <>
            Yes. Your money is held by Stripe - never by the seller and never by Nextmine - until
            you confirm your piece has arrived. Read more on our{' '}
            <NamedLink name="HowPaymentsWorkPage" className={css.link}>
              How payments work
            </NamedLink>{' '}
            page.
          </>
        ),
      },
    ],
  },
  {
    heading: 'ISO · Wanted - requests',
    items: [
      {
        q: 'What is ISO · Wanted?',
        a: (
          <>
            ISO means "in search of". Sold before you could tap buy on Oldmine? Post the one that
            got away - and let the circle of collectors surface her, or something that looks just
            like her. Posting a request is free, sellers can browse every request on the{' '}
            <NamedLink name="WantedPage" className={css.link}>
              ISO · Wanted
            </NamedLink>{' '}
            page, and we email you the moment a matching piece is listed.
          </>
        ),
      },
      {
        q: 'How do I post a request?',
        a: (
          <>
            Tap{' '}
            <NamedLink name="NewListingPage" className={css.link}>
              Sell now
            </NamedLink>{' '}
            and choose "In search of". Only a title, a short description and the type of jewelry are
            required - fill in metal, carat or age only if you know them. A photo is optional too,
            and you're welcome to reuse pictures from Oldmine or wherever you saw the piece. Your
            budget is also optional: leave it empty and sellers see an open budget.
          </>
        ),
      },
      {
        q: 'What happens when something matches?',
        a:
          'The moment a piece matching your request is listed, you get an email with a photo and a link. From there you buy exactly as normal - with the same payment protection, so your money is only released when you confirm the piece has arrived. The more details you fill in, the more precise your matches.',
      },
      {
        q: 'I have the piece someone is looking for - what do I do?',
        a:
          'Open the request and tap "I have this - list it for sale". List the piece as you normally would, and the requester is automatically notified by email the moment your listing is live. The sale itself works exactly like any other sale on Nextmine - same protection, same fees.',
      },
    ],
  },
  {
    heading: 'When your piece arrives',
    items: [
      {
        q: 'What do I do when my piece arrives?',
        a: (
          <>
            Open your order under{' '}
            <NamedLink name="InboxPage" params={{ tab: 'orders' }} className={css.link}>
              Orders &amp; sales
            </NamedLink>{' '}
            and tap "I've received my order". That confirms the purchase and starts the 14 day buyer
            protection period, after which the seller is paid. Please do it promptly - it's how the
            seller gets paid.
          </>
        ),
      },
      {
        q: 'What if the piece is not as described?',
        a:
          "Report it within 48 hours of confirming delivery. You and the seller first try to solve it together - most issues are sorted with a quick message. If you can't agree within 7 days, Nextmine steps in and decides the case.",
      },
      {
        q: 'How do returns work?',
        a:
          'If a dispute is decided in your favour, you return the piece with tracked shipping (you pay the return postage) and enter the tracking number on the order page. As soon as the seller confirms the piece is back, your money is refunded in full.',
      },
      {
        q: 'What if I forget to confirm?',
        a:
          "If you never confirm, the sale completes automatically 30 days after the seller marked the piece as shipped, and the seller is paid. So if something is wrong, don't wait - report it within 48 hours of receiving your piece.",
      },
    ],
  },
  {
    heading: 'Selling',
    items: [
      {
        q: 'How do I sell a piece?',
        a: (
          <>
            Tap{' '}
            <NamedLink name="NewListingPage" className={css.link}>
              Sell now
            </NamedLink>
            , add photos and details, set your price and shipping, and publish. Before your first
            sale you'll be guided through a short verification with Stripe (identity + where to send
            your money) - it protects both you and the buyers.
          </>
        ),
      },
      {
        q: 'How should I ship?',
        a: (
          <>
            You must ship within 7 days of the sale, with insured, tracked shipping, and enter the
            tracking number on the order page. Insurance is required - if a package is lost or
            damaged in the mail and wasn't insured, the loss is yours. See the full rules in our{' '}
            <NamedLink name="TermsOfServicePage" className={css.link}>
              Terms of Service
            </NamedLink>
            .
          </>
        ),
      },
      {
        q: 'When do I get paid?',
        a: (
          <>
            When the buyer confirms the piece has arrived, a 14 day buyer protection period begins,
            and your money is released at the end of it. If the buyer never confirms, the sale
            completes automatically 30 days after you marked the piece as shipped, and you are paid
            then. The wait protects you too: once the period has passed and you have been paid, the
            sale is closed. Payouts go straight to your bank account through Stripe (set up under{' '}
            <NamedLink name="StripePayoutPage" className={css.link}>
              Payout details
            </NamedLink>
            ).
          </>
        ),
      },
      {
        q: 'What does it cost to sell?',
        a:
          "Listing is free, and until 31 October 2026 selling is free too: Nextmine takes no commission on those sales. After that, Nextmine deducts a 15% commission from the sale price. Buyers pay a small 5% service fee on top - you'll see the exact amounts before you confirm anything.",
      },
      {
        q: 'Can I answer questions from buyers?',
        a:
          "Yes - buyers can ask public questions on your listing, and only you can answer them. Questions and answers are visible to everyone, so don't share contact details; keep the conversation on Nextmine, where the payment protection applies.",
      },
    ],
  },
  {
    heading: 'Account',
    items: [
      {
        q: 'What are Favorites?',
        a: (
          <>
            Tap the heart on any listing to save it. Your saved pieces live under{' '}
            <NamedLink name="FavoritesPage" className={css.link}>
              Favorites
            </NamedLink>{' '}
            in the bottom menu, so you can come back to them anytime - from any device.
          </>
        ),
      },
      {
        q: 'Do I need an account to buy?',
        a: (
          <>
            Yes - a free account is required so we can protect your payment, hold your order history
            and let you and the seller follow the sale.{' '}
            <NamedLink name="SignupPage" className={css.link}>
              Create one
            </NamedLink>{' '}
            in under a minute.
          </>
        ),
      },
      {
        q: 'How do I contact Nextmine?',
        a: (
          <>
            Write to{' '}
            <a href="mailto:hi@nextminejewelry.com" className={css.link}>
              hi@nextminejewelry.com
            </a>{' '}
            - we read everything and reply as fast as we can.
          </>
        ),
      },
    ],
  },
];

/**
 * FAQ / user guide - plain-language questions and answers about the whole flow.
 */
const FaqPage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const title = intl.formatMessage({ id: 'FaqPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <p className={css.eyebrow}>Nextmine · Help</p>
          <h1 className={css.title}>Questions &amp; answers</h1>
          <p className={css.meta}>
            Everything about buying, selling and getting paid - in plain words.
          </p>

          {faqSections.map(section => (
            <div key={section.heading} className={css.section}>
              <h2 className={css.h2}>{section.heading}</h2>
              {section.items.map(item => (
                <details key={item.q} className={css.item}>
                  <summary className={css.question}>{item.q}</summary>
                  <p className={css.answer}>{item.a}</p>
                </details>
              ))}
            </div>
          ))}

          <p className={css.outro}>
            Want the deeper details? Read{' '}
            <NamedLink name="HowPaymentsWorkPage" className={css.link}>
              How payments work
            </NamedLink>{' '}
            or the full{' '}
            <NamedLink name="TermsOfServicePage" className={css.link}>
              Terms of Service
            </NamedLink>
            .
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FaqPage;
